import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../helpers/apiClient';
import { API, BASE, URL_INVOICING_CONFIG, V1 } from '../constants/urls';
import type { InvoicingConfig } from '../models/invoicing';

const PROVIDERS = ['gestify', 'previxio'];

/**
 * La configurazione si rilegge al massimo ogni 10 minuti: dopo uno switch di
 * provider le pagine aperte si allineano senza ricaricare (ricaricare la
 * webapp resta il modo immediato).
 */
const CONFIG_TTL_MS = 10 * 60 * 1000;

let cachedConfig: { value: InvoicingConfig; expiresAt: number } | undefined;
let pendingRequest: Promise<InvoicingConfig> | undefined;

const isInvoicingConfig = (value: unknown): value is InvoicingConfig => {
  const config = value as InvoicingConfig | undefined;
  return (
    !!config &&
    PROVIDERS.includes(config.provider) &&
    typeof config.features === 'object' &&
    config.features !== null
  );
};

const freshCachedConfig = (): InvoicingConfig | undefined =>
  cachedConfig && cachedConfig.expiresAt > Date.now()
    ? cachedConfig.value
    : undefined;

/** Legge la configurazione (cache con scadenza; le richieste concorrenti condividono la stessa promise). */
export const fetchInvoicingConfig = (): Promise<InvoicingConfig> => {
  const cached = freshCachedConfig();
  if (cached) return Promise.resolve(cached);
  if (!pendingRequest) {
    pendingRequest = apiClient
      .get<InvoicingConfig>({
        url: `${BASE}${API}${V1}${URL_INVOICING_CONFIG}`,
      })
      .then((response) => {
        if (!response?.ok || !isInvoicingConfig(response.data)) {
          throw new Error('Configurazione della fatturazione non disponibile');
        }
        cachedConfig = {
          value: response.data,
          expiresAt: Date.now() + CONFIG_TTL_MS,
        };
        return response.data;
      })
      .finally(() => {
        pendingRequest = undefined;
      });
  }
  return pendingRequest;
};

interface InvoicingConfigState {
  config?: InvoicingConfig;
  loading: boolean;
  error?: Error;
}

interface InvoicingConfigHook extends InvoicingConfigState {
  /** Rilegge la configurazione dopo un errore (bottone "Riprova"). */
  reload: () => void;
}

/** Provider di fatturazione attivo e funzioni visibili, da `GET /invoicing/config`. */
export const useInvoicingConfig = (): InvoicingConfigHook => {
  const [state, setState] = useState<InvoicingConfigState>(() => {
    const cached = freshCachedConfig();
    return { config: cached, loading: !cached };
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // La cache può essersi riempita dopo il primo render (da un altro
    // componente): senza questo aggiornamento lo stato resterebbe "loading".
    const cached = freshCachedConfig();
    if (cached) {
      setState({ config: cached, loading: false });
      return;
    }
    let active = true;
    setState((current) => ({ ...current, loading: true, error: undefined }));
    fetchInvoicingConfig()
      .then((config) => active && setState({ config, loading: false }))
      .catch((error: Error) => active && setState({ loading: false, error }));
    return () => {
      active = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, reload };
};
